import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import {
  formatPrice,
  getMaximumAmountForUsdCeiling,
  getMinimumAmountForUsdFloor,
} from "helpers/common";
import { myAccount } from "Routes/paths";
import {
  BILLING_FREQUENCY_OPTIONS,
  getBillingFrequencyLabel,
  getEstimatedInstallmentAmount,
  INSTALLMENT_PAYMENT_TYPE,
  normalizeBillingFrequency,
  ONE_TIME_PAYMENT_TYPE,
} from "../packagePaymentHelpers";

const PRICE_USD_MIN = 0.5;
const PRICE_USD_MAX = 999999.99;

const formatCurrencyAmount = (value) =>
  Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const buildPriceRangeMessage = (currencySign, currencyCode, min, max) => {
  const range = `Must be between ${currencySign}${formatCurrencyAmount(
    min
  )} and ${currencySign}${formatCurrencyAmount(max)}`;

  return currencyCode === "USD" ? `${range}.` : `${range} (${currencyCode}).`;
};

const validation = Yup.object({
  package_name: Yup.string().required("Package name is required"),
  package_desc: Yup.string().required("Package description is required"),
  package_price: Yup.number()
    .typeError("Package price must be a number")
    .positive("Price must be greater than 0")
    .required("Package price is required"),
  session_count: Yup.number()
    .typeError("Session count must be a number")
    .integer("Session count must be a whole number")
    .min(1, "Keep it between 1-100")
    .max(100, "Keep it between 1-100")
    .required("Session count is required"),
  currency_code: Yup.string().optional(),
  currency_sign: Yup.string().optional(),
  payment_type: Yup.string()
    .oneOf([ONE_TIME_PAYMENT_TYPE, INSTALLMENT_PAYMENT_TYPE])
    .required("Payment type is required"),
  billing_frequency: Yup.string().when("payment_type", {
    is: INSTALLMENT_PAYMENT_TYPE,
    then: (schema) =>
      schema
        .oneOf(BILLING_FREQUENCY_OPTIONS.map((option) => option.value))
        .required("Billing frequency is required"),
    otherwise: (schema) => schema.strip(),
  }),
  number_of_rebills: Yup.number().when("payment_type", {
    is: INSTALLMENT_PAYMENT_TYPE,
    then: (schema) =>
      schema
        .typeError("Number of installments must be a number")
        .integer("Number of installments must be a whole number")
        .min(1, "Number of installments must be at least 1")
        .required("Number of installments is required"),
    otherwise: (schema) => schema.strip(),
  }),
});

const AddPackagePop = ({
  addPackagePop,
  toggleAddPackagePop,
  currentPacakge,
  submitData_,
  stripeConnected,
}) => {
  const editMode = !!currentPacakge;
  const stripeConnectPath = `${myAccount}?tab=3rdParty`;

  return (
    <Formik
      initialValues={{
        package_type: "session",
        package_name: currentPacakge?.package_name || "",
        package_desc: currentPacakge?.package_desc || "",
        package_price: currentPacakge?.package_price || "",
        session_count: currentPacakge?.session_count || 1,
        currency_code: currentPacakge?.currency_code || "USD",
        currency_sign: currentPacakge?.currency_sign || "$",
        payment_type: currentPacakge?.payment_type || ONE_TIME_PAYMENT_TYPE,
        billing_frequency: normalizeBillingFrequency(
          currentPacakge?.billing_frequency
        ) || "",
        number_of_rebills: currentPacakge?.number_of_rebills || "",
      }}
      enableReinitialize
      validationSchema={validation}
      onSubmit={async (values, formikHelpers) => {
        const { setSubmitting, setFieldError, setFieldTouched, setStatus } =
          formikHelpers;

        setStatus(null);

        const [minAmount, maxAmount] = await Promise.all([
          getMinimumAmountForUsdFloor(values.currency_code),
          getMaximumAmountForUsdCeiling(values.currency_code),
        ]);

        const enteredPrice = parseFloat(values.package_price);
        const isOutOfRange =
          minAmount == null ||
          maxAmount == null ||
          Number.isNaN(enteredPrice) ||
          enteredPrice < minAmount ||
          enteredPrice > maxAmount;

        if (isOutOfRange) {
          setFieldTouched("package_price", true);
          setFieldError(
            "package_price",
            buildPriceRangeMessage(
              values.currency_sign,
              values.currency_code,
              minAmount ?? PRICE_USD_MIN,
              maxAmount ?? PRICE_USD_MAX
            )
          );
          setSubmitting(false);
          return;
        }

        await submitData_(values, formikHelpers);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        status,
      }) => {
        const [priceOutOfRange, setPriceOutOfRange] = useState(false);
        const [minConvertedAmount, setMinConvertedAmount] = useState(null);
        const [maxConvertedAmount, setMaxConvertedAmount] = useState(null);

        const installmentEstimate = getEstimatedInstallmentAmount(
          values.package_price,
          values.number_of_rebills
        );
        const showInstallmentFields =
          values.payment_type === INSTALLMENT_PAYMENT_TYPE;
        const showInstallmentPreview =
          showInstallmentFields &&
          values.package_price !== "" &&
          !!values.billing_frequency &&
          values.number_of_rebills !== "" &&
          !Number.isNaN(installmentEstimate) &&
          installmentEstimate !== null;
        const totalInstallments = Number(values.number_of_rebills);
        const remainingInstallments = Number.isFinite(totalInstallments)
          ? Math.max(totalInstallments - 1, 0)
          : 0;
        const billingFrequencyLabel = getBillingFrequencyLabel(
          values.billing_frequency
        ).toLowerCase();

        useEffect(() => {
          const checkPriceRange = async () => {
            if (!values.package_price || isNaN(values.package_price)) {
              setPriceOutOfRange(false);
              setMinConvertedAmount(null);
              setMaxConvertedAmount(null);
              return;
            }

            const [convertedMin, convertedMax] = await Promise.all([
              getMinimumAmountForUsdFloor(values.currency_code),
              getMaximumAmountForUsdCeiling(values.currency_code),
            ]);

            if (convertedMin == null || convertedMax == null) {
              setPriceOutOfRange(false);
              setMinConvertedAmount(null);
              setMaxConvertedAmount(null);
              return;
            }

            const enteredPrice = parseFloat(values.package_price);

            setMinConvertedAmount(convertedMin);
            setMaxConvertedAmount(convertedMax);
            setPriceOutOfRange(
              Number.isNaN(enteredPrice)
                ? false
                : enteredPrice < convertedMin || enteredPrice > convertedMax
            );
          };

          checkPriceRange();
        }, [values.package_price, values.currency_code]);

        const handlePaymentTypeChange = (event) => {
          const nextPaymentType = event.target.value;

          setFieldValue("payment_type", nextPaymentType);
          setFieldTouched("payment_type", true, false);

          if (nextPaymentType === ONE_TIME_PAYMENT_TYPE) {
            setFieldValue("billing_frequency", "", false);
            setFieldValue("number_of_rebills", "", false);
            setFieldTouched("billing_frequency", false, false);
            setFieldTouched("number_of_rebills", false, false);
          }
        };

        return (
          <Modal
            show={addPackagePop}
            onHide={toggleAddPackagePop}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            scrollable="true"
            className="FormPop"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {editMode ? "Update Package" : "New Package"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col lg="12" className="my-2">
                    <label htmlFor="" className="form-label m-0 fw-sbold">
                      Package Title <span className="text-muted">(required)</span>
                    </label>
                    <p className="m-0">
                      This is the title of your package. It will be visible to
                      clients when you select the 'send to client' option.
                    </p>
                    <input
                      type="text"
                      className={`form-control ${
                        errors.package_name && touched.package_name
                          ? "is-invalid"
                          : ""
                      }`}
                      name="package_name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.package_name}
                    />
                    {errors.package_name && touched.package_name && (
                      <p className="invalid-feedback">{errors.package_name}</p>
                    )}
                  </Col>
                  <Col lg="12" className="my-2">
                    <label htmlFor="" className="form-label m-0 fw-sbold">
                      Package Description{" "}
                      <span className="text-muted">(required)</span>
                    </label>
                    <p className="m-0">
                      Provide a short description of your package.
                    </p>
                    <textarea
                      name="package_desc"
                      id=""
                      rows="4"
                      className={`form-control ${
                        errors.package_desc && touched.package_desc
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.package_desc}
                    ></textarea>
                    {errors.package_desc && touched.package_desc && (
                      <p className="invalid-feedback">{errors.package_desc}</p>
                    )}
                  </Col>
                  <Col lg="12" className="my-2">
                    <label htmlFor="" className="form-label m-0 fw-sbold">
                      Package Price <span className="text-muted">(required)</span>
                    </label>
                    <p className="m-0">
                      The price of the package. This should always be the total
                      package price.
                    </p>
                    <div className="py-2">
                      <div className="input-group mb-3">
                        <div className="input-group-prepend">
                          <Form.Select
                            className="form-control border-right-0"
                            name="currency_code"
                            value={`${values.currency_sign}${values.currency_code}`}
                            onChange={(event) => {
                              const selected = event.target.value;
                              const sign = selected.substring(0, 1);
                              const code = selected.substring(1);
                              setFieldValue("currency_sign", sign);
                              setFieldValue("currency_code", code);
                            }}
                          >
                            <option value="$USD">$USD</option>
                            <option value="$CAD">$CAD</option>
                            <option value="$AUD">$AUD</option>
                            <option value="$NZD">$NZD</option>
                            <option value="£GBP">£GBP</option>
                            <option value="€EUR">€EUR</option>
                            <option value="¥JPY">¥JPY</option>
                          </Form.Select>
                        </div>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          name="package_price"
                          className={`form-control no-spinner ${
                            errors.package_price && touched.package_price
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Amount"
                          onChange={(event) => {
                            const value = parseFloat(event.target.value);

                            if (value < 0) {
                              return;
                            }

                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                          value={values.package_price}
                        />
                      </div>
                    </div>
                    {errors.package_price && touched.package_price && (
                      <p className="invalid-feedback">{errors.package_price}</p>
                    )}

                    {priceOutOfRange &&
                      minConvertedAmount &&
                      maxConvertedAmount &&
                      !errors.package_price && (
                        <p className="text-danger">
                          {buildPriceRangeMessage(
                            values.currency_sign,
                            values.currency_code,
                            minConvertedAmount,
                            maxConvertedAmount
                          )}
                        </p>
                      )}

                    {values.package_price !== "" &&
                      !Number.isNaN(parseFloat(values.package_price)) &&
                      !showInstallmentFields && (
                        <p className="m-0">
                          A one-time payment of{" "}
                          <span className="fw-sbold">
                            {values.currency_sign}
                            {formatPrice(values.package_price)} (
                            {values.currency_code})
                          </span>{" "}
                          will be required for clients to sign up for this
                          package.
                        </p>
                      )}
                  </Col>
                  <Col lg="12" className="my-2">
                    <label htmlFor="" className="form-label m-0 fw-sbold">
                      Payment Type <span className="text-muted">(required)</span>
                    </label>
                    <p className="m-0">
                      Choose whether clients pay the total price upfront or in
                      installments.
                    </p>
                    <Form.Select
                      name="payment_type"
                      className={`form-control ${
                        errors.payment_type && touched.payment_type
                          ? "is-invalid"
                          : ""
                      }`}
                      value={values.payment_type}
                      onChange={handlePaymentTypeChange}
                      onBlur={handleBlur}
                    >
                      <option value={ONE_TIME_PAYMENT_TYPE}>One-time</option>
                      <option
                        value={INSTALLMENT_PAYMENT_TYPE}
                        disabled={
                          !stripeConnected &&
                          values.payment_type !== INSTALLMENT_PAYMENT_TYPE
                        }
                      >
                        Installment
                      </option>
                    </Form.Select>
                    {errors.payment_type && touched.payment_type && (
                      <p className="invalid-feedback">{errors.payment_type}</p>
                    )}
                    {!stripeConnected && (
                      <p className="m-0 mt-2 text-muted">
                        Installment plans require an active Stripe connection.{" "}
                        <Link to={stripeConnectPath}>Connect Stripe</Link>
                      </p>
                    )}
                  </Col>

                  {showInstallmentFields && (
                    <>
                      <Col lg="12" className="my-2">
                        <label htmlFor="" className="form-label m-0 fw-sbold">
                          Billing Frequency{" "}
                          <span className="text-muted">(required)</span>
                        </label>
                        <p className="m-0">
                          Choose how often the installment charges recur.
                        </p>
                        <Form.Select
                          name="billing_frequency"
                          className={`form-control ${
                            errors.billing_frequency && touched.billing_frequency
                              ? "is-invalid"
                              : ""
                          }`}
                          value={values.billing_frequency}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value="">Select billing frequency</option>
                          {BILLING_FREQUENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                        {errors.billing_frequency &&
                          touched.billing_frequency && (
                            <p className="invalid-feedback">
                              {errors.billing_frequency}
                            </p>
                          )}
                      </Col>
                      <Col lg="12" className="my-2">
                        <label htmlFor="" className="form-label m-0 fw-sbold">
                          Number of Installments{" "}
                          <span className="text-muted">(required)</span>
                        </label>
                        <p className="m-0">
                          The first installment is charged upfront. Total charges
                          will equal this number.
                        </p>
                        <input
                          type="number"
                          min={1}
                          step="1"
                          name="number_of_rebills"
                          className={`form-control no-spinner ${
                            errors.number_of_rebills &&
                            touched.number_of_rebills
                              ? "is-invalid"
                              : ""
                          }`}
                          onChange={(event) => {
                            const value = parseInt(event.target.value, 10);

                            if (!Number.isNaN(value) && value < 1) {
                              return;
                            }

                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                          value={values.number_of_rebills}
                        />
                        {errors.number_of_rebills &&
                          touched.number_of_rebills && (
                            <p className="invalid-feedback">
                              {errors.number_of_rebills}
                            </p>
                          )}
                      </Col>
                    </>
                  )}

                  {showInstallmentFields && (
                    <Col lg="12" className="my-2">
                      <div
                        className="p-3 rounded"
                        style={{ background: "#fff8f6", border: "1px solid #f6d8cf" }}
                      >
                        <p className="m-0 fw-sbold">Installment Preview</p>
                        {showInstallmentPreview ? (
                          <p className="m-0 pt-1">
                            Your member will be charged{" "}
                            <span className="fw-sbold">
                              {values.currency_sign}
                              {formatPrice(installmentEstimate)} (
                              {values.currency_code})
                            </span>{" "}
                            immediately
                            {remainingInstallments > 0 ? (
                              <>
                                , and then {remainingInstallments}{" "}
                                {billingFrequencyLabel} payment
                                {remainingInstallments === 1 ? "" : "s"} of{" "}
                                <span className="fw-sbold">
                                  {values.currency_sign}
                                  {formatPrice(installmentEstimate)} (
                                  {values.currency_code})
                                </span>
                              </>
                            ) : null}
                            . The total amount paid for the product will be{" "}
                            <span className="fw-sbold">
                              {values.currency_sign}
                              {formatPrice(values.package_price)} (
                              {values.currency_code})
                            </span>
                            .
                          </p>
                        ) : (
                          <p className="m-0 pt-1 text-muted">
                            Enter the total price, billing frequency, and number
                            of installments to preview the estimated payment
                            breakdown.
                          </p>
                        )}
                      </div>
                    </Col>
                  )}

                  {showInstallmentFields &&
                    status?.stripeInstallmentError &&
                    status?.stripeInstallmentErrorMessage && (
                      <Col lg="12" className="my-2">
                        <div className="alert alert-danger mb-0">
                          <p className="m-0">
                            {status.stripeInstallmentErrorMessage}{" "}
                            <Link to={stripeConnectPath}>Connect Stripe</Link>
                          </p>
                        </div>
                      </Col>
                    )}

                  <Col lg="12" className="my-2">
                    <label htmlFor="" className="form-label m-0 fw-sbold">
                      Total Sessions <span className="text-muted">(required)</span>
                    </label>
                    <p className="m-0">
                      Total number of sessions provided in this package
                    </p>
                    <input
                      type="number"
                      className={`form-control no-spinner ${
                        errors.session_count && touched.session_count
                          ? "is-invalid"
                          : ""
                      }`}
                      min={1}
                      step="1"
                      name="session_count"
                      onChange={(event) => {
                        const value = parseInt(event.target.value, 10);

                        if (!Number.isNaN(value) && value < 1) {
                          return;
                        }

                        handleChange(event);
                      }}
                      onBlur={handleBlur}
                      value={values.session_count}
                    />
                    {errors.session_count && touched.session_count && (
                      <p className="invalid-feedback">{errors.session_count}</p>
                    )}
                  </Col>
                  <Col>
                    <div className="d-flex gap-10 FormbtnWrpper">
                      <Button
                        className="d-flex align-items-center justify-content-center commonBtn btnSm"
                        onClick={toggleAddPackagePop}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="d-flex align-items-center justify-content-center commonBtn btnSm"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Please Wait..." : "Submit"}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default AddPackagePop;
